import { format } from 'date-fns';
import { produce } from 'immer';
import { CircleHelp, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  productFiltersInitialState,
  productFiltersReducer,
} from './productFiltersReducer';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { RangeSlider } from '@/components/ui/RangeSlider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/Sheet';
import { Switch } from '@/components/ui/Switch';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateMerchandisingCollectionSlice } from '@/hooks/useUpdateMerchandisingCollectionSlice';
import { formatPriceByPlatform, priceFormatterByLocale } from '@/lib/utils';
import {
  MerchandisingCollectionSlice,
  SliceFilters,
} from '@/services/MerchandisingCollectionsService';

interface ProductSliceFiltersProps {
  slice: MerchandisingCollectionSlice;
}

// Parse seguro para o release date
function toDateSafe(date: Date) {
  try {
    return format(date, 'yyyy-MM-dd');
  } catch {
    return format(new Date(), 'yyyy-MM-dd');
  }
}

// Parse seguro que garante número finito (em reais)
function toNumberSafe(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function ProductFilters({ slice }: ProductSliceFiltersProps) {
  const [state, dispatch] = useReducer(
    productFiltersReducer,
    slice?.filters ?? productFiltersInitialState,
  );
  const [isReset, setIsReset] = useState(false);
  const [errors, setErrors] = useState<{ type: string; message: string }[]>([]);
  const [priceBounds, setPriceBounds] = useState<{ min: number; max: number }>({
    min: 0,
    max: 0,
  });

  const { user } = useAuth();

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.SliceProducts.components.ProductFilters',
  });

  const { updateMerchandisingCollectionSlice, isLoading } =
    useUpdateMerchandisingCollectionSlice();

  const isDisabled = isLoading;

  const calculatePriceBounds = useCallback(() => {
    const platform = user!.customer.platform;

    const minRaw = toNumberSafe(slice.additionalInfo?.filters?.minPrice);
    const maxRaw = toNumberSafe(slice.additionalInfo?.filters?.maxPrice);

    const min = toNumberSafe(formatPriceByPlatform(minRaw, platform));
    const max = toNumberSafe(formatPriceByPlatform(maxRaw, platform));

    const safeMin = Math.max(0, Math.min(min, max));
    const safeMax = Math.max(safeMin, Math.max(min, max));

    setPriceBounds({ min: safeMin, max: safeMax });

    return { safeMin, safeMax };
  }, [slice, user]);

  async function handleApply() {
    try {
      // clamp defensivo antes de salvar
      const min = priceBounds.min;
      const max = priceBounds.max;
      const from = Math.max(min, Math.min(state.price.range.from, max));
      const to = Math.max(from, Math.min(state.price.range.to, max));

      await updateMerchandisingCollectionSlice({
        sliceId: slice.id,
        collectionId: slice.collectionId,
        dto: {
          name: slice.name,
          startsAt: slice.startsAt,
          endsAt: slice.endsAt,
          filters: isReset
            ? null
            : {
                ...state,
                price: { active: state.price.active, range: { from, to } },
              },
        },
      });

      toast.success(t('toast.succces'));
    } catch (error) {
      toast.error(t('toast.error'));
    }
  }

  async function handleReset() {
    const { safeMin, safeMax } = calculatePriceBounds();

    dispatch({ type: 'reset' });
    dispatch({
      type: 'price',
      payload: { active: false, range: { from: safeMin, to: safeMax } },
    });
  }

  // validação de datas
  useEffect(() => {
    setErrors(
      produce((draft) => {
        const isStartAfterEnd = 'isStartAfterEnd';

        if (state.releasePeriod.startsAt > state.releasePeriod.endsAt) {
          const errorIndex = draft.findIndex((e) => e.type === isStartAfterEnd);
          if (errorIndex === -1) {
            draft.push({
              type: isStartAfterEnd,
              message: t('errors.startsAtError'),
            });
          }
        } else {
          const errorIndex = draft.findIndex((e) => e.type === isStartAfterEnd);
          if (errorIndex >= 0) {
            draft.splice(errorIndex, 1);
          }
        }
      }),
    );
  }, [state, t]);

  useEffect(() => {
    const { safeMin, safeMax } = calculatePriceBounds();

    if (!slice.filters) {
      dispatch({
        type: 'price',
        payload: {
          active: false,
          range: { from: safeMin, to: safeMax },
        },
      });
    }
  }, [calculatePriceBounds, slice, user]);

  useEffect(() => {
    setIsReset(!Object.values(state).some((v) => v.active));
  }, [state]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" className="gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          {t('sheetTrigger')}
        </Button>
      </SheetTrigger>

      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('sheetTitle')}</SheetTitle>
          <SheetDescription />
        </SheetHeader>

        <div className="grid gap-6 py-4">
          {/* Full Size Run */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Label htmlFor="broken-stock">{t('filters.fullSizeRun')}</Label>
              <Popover>
                <PopoverTrigger>
                  <CircleHelp className="h-4 w-4 cursor-pointer text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2">
                  <p className="text-sm text-muted-foreground">
                    {t('filters.fullSizeRunDescription')}
                  </p>
                </PopoverContent>
              </Popover>
            </div>

            <Switch
              disabled={isDisabled}
              id="broken-stock"
              checked={state.brokenStock.active}
              onCheckedChange={(checked) =>
                dispatch({ type: 'brokenStock', payload: { active: checked } })
              }
            />
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label htmlFor="discount-filter">{t('filters.discount')}</Label>

            <Select
              disabled={isDisabled}
              value={state.discount.type}
              onValueChange={(value: SliceFilters['discount']['type']) =>
                dispatch({
                  type: 'discount',
                  payload: {
                    type: value,
                    range: { from: 0, to: 100 },
                    active: true,
                  },
                })
              }
            >
              <SelectTrigger id="discount-filter">
                <SelectValue placeholder="Select the discount type" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="ALL">{t('filters.allProducts')}</SelectItem>
                <SelectItem value="WITH_DISCOUNT">
                  {t('filters.productsWithDiscount')}
                </SelectItem>
                <SelectItem value="WITHOUT_DISCOUNT">
                  {t('filters.productsWithoutDiscount')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {state.discount.type === 'WITH_DISCOUNT' && (
            <div className="space-y-4">
              <Label htmlFor="discount-range">
                {t('filters.discountRange')}
              </Label>

              <RangeSlider
                disabled={isDisabled}
                id="discount-range"
                className="w-full"
                min={0}
                max={100}
                step={1}
                value={[state.discount.range.from, state.discount.range.to]}
                onValueChange={(value) =>
                  dispatch({
                    type: 'discount',
                    payload: {
                      type: 'WITH_DISCOUNT',
                      range: { from: value[0], to: value[1] },
                      active: true,
                    },
                  })
                }
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{state.discount.range.from}%</span>
                <span>{state.discount.range.to}%</span>
              </div>
            </div>
          )}

          {/* Price Range */}
          <div className="space-y-4">
            <Label htmlFor="price-range">{t('filters.priceRange')}</Label>

            <RangeSlider
              disabled={isDisabled}
              id="price-range"
              min={priceBounds.min}
              max={priceBounds.max}
              value={[state.price.range.from, state.price.range.to]}
              onValueChange={(value) => {
                const [from, to] = value;
                const min = priceBounds.min;
                const max = priceBounds.max;

                const clampedFrom = Math.max(min, Math.min(from, max));
                const clampedTo = Math.max(clampedFrom, Math.min(to, max));

                dispatch({
                  type: 'price',
                  payload: {
                    range: { from: clampedFrom, to: clampedTo },
                    active: true,
                  },
                });
              }}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>
                {priceFormatterByLocale(
                  state.price.range.from,
                  user!.customer.language,
                )}
              </span>
              <span>
                {priceFormatterByLocale(
                  state.price.range.to,
                  user!.customer.language,
                )}
              </span>
            </div>
          </div>

          {/* Low Stock */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="low-stock">{t('filters.lowStock')}</Label>

              <Switch
                disabled={isDisabled}
                id="low-stock"
                checked={state.lowStock.active}
                onCheckedChange={(checked) =>
                  dispatch({
                    type: 'lowStock',
                    payload: {
                      ...state.lowStock,
                      active: checked,
                    },
                  })
                }
              />
            </div>

            {state.lowStock.active && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="limit">{t('filters.lowStockLimit')}</Label>

                  <Popover>
                    <PopoverTrigger>
                      <CircleHelp className="h-4 w-4 cursor-pointer text-muted-foreground" />
                    </PopoverTrigger>

                    <PopoverContent className="w-80 p-2">
                      <p className="text-sm text-muted-foreground">
                        {t('filters.lowStockDescription')}
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>

                <Input
                  disabled={isDisabled}
                  id="limit"
                  type="number"
                  className="w-full"
                  value={state.lowStock.stockLimit}
                  onChange={(event) =>
                    dispatch({
                      type: 'lowStock',
                      payload: {
                        stockLimit: parseInt(event.target.value),
                        active: true,
                      },
                    })
                  }
                />
              </div>
            )}
          </div>

          {/* Release Period */}
          <div className="hidden space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="release-period">Release Period</Label>

              <Switch
                disabled={isDisabled}
                id="release-period"
                checked={state.releasePeriod.active}
                onCheckedChange={(checked) =>
                  dispatch({
                    type: 'releasePeriod',
                    payload: {
                      ...state.releasePeriod,
                      active: checked,
                    },
                  })
                }
              />
            </div>

            {state.releasePeriod.active && (
              <div className="flex space-x-2">
                <Input
                  disabled={isDisabled}
                  type="date"
                  className="flex-1"
                  value={toDateSafe(state.releasePeriod.startsAt)}
                  onChange={(event) =>
                    dispatch({
                      type: 'releasePeriod',
                      payload: {
                        startsAt: new Date(event.target.value),
                        endsAt: state.releasePeriod.endsAt,
                        active: true,
                      },
                    })
                  }
                />

                <Input
                  disabled={isDisabled}
                  type="date"
                  className="flex-1"
                  value={toDateSafe(state.releasePeriod.endsAt)}
                  onChange={(event) =>
                    dispatch({
                      type: 'releasePeriod',
                      payload: {
                        startsAt: state.releasePeriod.startsAt,
                        endsAt: new Date(event.target.value),
                        active: true,
                      },
                    })
                  }
                />
              </div>
            )}
          </div>

          {/* Errors */}
          <div className="space-y-2">
            {errors.map((error) => (
              <p key={error.type} className="text-sm text-red-500">
                {error.message}
              </p>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={isDisabled || errors.length > 0}
              className="w-full"
              onClick={handleApply}
            >
              {t('filters.applyButtonLabel')}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isLoading}
              onClick={handleReset}
            >
              {t('filters.clearButtonLabel')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
